import {connect} from 'react-redux';

import {RecipientRolesEnum} from '../../../../libs/shared/core/src/models/api/recipientModel';
import {globalNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ContactDetailsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {ContactInformationContact} from '../../../../srcCommon/inboxes/contactDetails/contactDetailsHelpers';
import {getContactSourceFromMessageType, getMessageTypeFromChannelType} from '../../../helpers/modelHelpers';
import {CardAttributeUpdateAction} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {VisualRecipientModel} from '../../../models/api/visualRecipientModel';
import {AppState} from '../../../store/appState';
import {ThunkAction} from '../../../store/appThunk';
import {makeGetSortedComposeChannels} from '../../../store/data/channels/channelsSelectors';
import {composeMessageAction} from '../../../store/data/messages/draftMessagesActions';
import {popoutContactDetailsAction} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {
  makeGetContactDetailsPanel,
  makeGetDuplicateCard,
} from '../../../store/panels/contacts/contactDetailsPanelSelectors';
import {ContactDetailsPanelLocationsEnum} from '../../../store/panels/contacts/contactDetailsPanelState';
import {Contact} from './contact';

/*
 * Props.
 */

interface ContactContainerProps {
  panelId: ContactDetailsPanelId;
  card: CardModel | undefined;
  contact: ContactInformationContact;
  isMainHandle: boolean;
  selectedVisualContact?: VisualContactModel;
  updateCardAttributeAsync: (action: CardAttributeUpdateAction) => Promise<void>;
  updateUnsavedCard: (action: CardAttributeUpdateAction) => void;
  location?: ContactDetailsPanelLocationsEnum;
}

/*
 * Selectors.
 */

function getSelectedCard(_state: AppState, props: ContactContainerProps) {
  return props.card;
}

function getContact(_state: AppState, props: ContactContainerProps) {
  return props.contact;
}

function makeGetHasChannel() {
  return createSelector([makeGetSortedComposeChannels({}), getContact], (channels, contact) =>
    channels.some(
      (channel) =>
        getContactSourceFromMessageType(getMessageTypeFromChannelType(channel.type)) === contact.source &&
        channel.behaviors.supportedContactSources.includes(contact.source),
    ),
  );
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactContainerProps>()(() => {
  const getContactDetailsPanel = makeGetContactDetailsPanel();
  const getDuplicateCard = makeGetDuplicateCard(getContactDetailsPanel, getSelectedCard);

  return {
    duplicateCard: getDuplicateCard,
    hasChannel: makeGetHasChannel(),
  };
});

const mapDispatchToProps = makeMapDispatchToProps<ContactContainerProps>()((props) => ({
  onComposeMessage:
    (contact: ContactInformationContact): ThunkAction =>
    (dispatch) => {
      // The namespace doesn't matter here since we're just opening the composer
      const visualRecipient: VisualRecipientModel = {
        contact,
        namespace: globalNamespace,
        role: RecipientRolesEnum.TO,
      };
      dispatch(composeMessageAction({recipients: [visualRecipient], shouldForcePopout: true}));
    },
  openContactDetailsPopUp: (cardId: number) =>
    popoutContactDetailsAction({
      parentPanelId: props.panelId,
      selectedContactUid: props.selectedVisualContact?.contact.uid,
      selectedCardId: cardId,
    }),
}));

export const ContactContainer = connect(mapStateToProps, mapDispatchToProps)(Contact);
